import { HOST_NAME } from "src/module-federation-runtime/constants";

/**
 * Factory for a zero-prop React fallback component.
 *
 * Notes:
 * - React is lazy-imported to keep this path cold until an error occurs.
 * - The component logs a one-time warning on mount to help identify
 *   unsupported “side-loaded” usage (bypassing the SDK).
 * - The rendered UI is intentionally generic; the SDK will usually
 *   replace this with richer, contextual fallback UI.
 */
export const createFallbackComponent = async ({ id }: { id: string }) => {
    const React = await import("react");

    // Last-resort error UI fallback for callers who bypass the SDK and import through module
    // federation directly -- either accidentally or because they don’t know better.
    const FallbackComponent = React.memo(() => {
        const errorMessage = `${HOST_NAME} Module loading failed for module ${id}. Please try again later`;
        const SDKUrl = "https://modules.console.kale.amazon.dev/docs/about#sdk";

        React.useEffect(function warnOnMount() {
            // One-time console warning to discourage side-loading and point to SDK usage.
            console.warn(
                `${HOST_NAME} WARNING: Module ${id} is being side loaded. ` +
                    `Please follow proper guidance and only render modules using the SDK: ${SDKUrl}`
            );
        }, []);

        return (
            <div
                style={{
                    padding: "16px",
                    border: "1px solid #ffa39e",
                    borderRadius: "4px",
                    backgroundColor: "#fff1f0",
                    color: "#cf1322",
                }}
            >
                {errorMessage}
            </div>
        );
    });

    FallbackComponent.displayName = "FallbackComponent";

    return FallbackComponent;
};
